import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  CheckoutWrapperComponent,
  SelectedCustomerService,
  CartTotalsService,
  TotalObject,
  Customer,
  CheckoutObject,
  AuthService
} from 'gung-standard';
import { combineLatest, first, forkJoin, map, mergeMap, of, Subject, switchMap, takeUntil } from 'rxjs';
import { JeevesCheckCreditService } from 'gung-standard-jeeves';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HlDisplayCreditBlockedService } from '../../services/hl-display-credit-blocked.service';
import {
  CheckoutSettingsGroup,
  HlDisplayPortalFeaturesService,
  PriceSettingsGroup
} from '../../services/hl-display-portal-features.service';
import { HlCheckoutButtonsPortalComponent } from '../checkout/hl-checkout-buttons-portal/hl-checkout-buttons-portal.component';
import { HlDisplayAdditionalCostsService } from '../../services/hl-display-additional-costs.service';
import { HLDisplayCustomFreight, HlDisplayFreightService } from '../../services/hl-display-freight.service';
import { HLDisplayCheckoutUtilService } from '../../services/hldisplay-checkout-util.service';

@Component({
  selector: 'app-hl-checkout-wrapper',
  templateUrl: './hl-checkout-wrapper.component.html',
  styleUrls: ['./hl-checkout-wrapper.component.css'],
  standalone: false
})
export class HlCheckoutWrapperComponent extends CheckoutWrapperComponent implements OnInit, OnDestroy {
  protected selectCustomerService = inject(SelectedCustomerService);
  protected cartTotalsService = inject(CartTotalsService);
  protected jeevesCheckCreditService = inject(JeevesCheckCreditService);
  protected translateService = inject(TranslateService);
  protected creditBlockedService = inject(HlDisplayCreditBlockedService);
  protected portalFeatureService = inject(HlDisplayPortalFeaturesService);
  protected hlDisplayAdditionalCostsService = inject(HlDisplayAdditionalCostsService);
  protected hlDisplayFreightService = inject(HlDisplayFreightService);
  protected authService = inject(AuthService);
  protected hlDisplayCheckoutUtilService = inject(HLDisplayCheckoutUtilService);

  isCreditHardBlock: boolean = false;
  isCreditSoftBlock: boolean = false;
  minOrderValue: number;

  useTradePolicy: boolean = false;
  freightCost: number;
  freightCostMin: number;
  adminCharge: number;
  adminChargeMin: number;
  brokenBoxFee: number = 0;
  orderTotalValue: number;

  customFreight: HLDisplayCustomFreight;

  currencySymbol: string;

  currencyCode: string = '';
  customer: Customer;
  creditLeft: number = 0;
  totals: TotalObject = { totalQuantity: 0, totalValue: 0, totalRows: 0 };
  protected modalOpen: boolean = false;
  protected unsubscribeTotals: Subject<void> = new Subject();

  ngOnInit(): void {
    forkJoin({
      isCustomerService: this.authService.getHasRoles('CUSTOMER_SERVICE').pipe(first()),
      checkoutSettingsGroup: this.portalFeatureService.getPortalFeatureGroup('orderCheckoutSettingsGroup').pipe(
        first(),
        map(checkoutSettingsGroup => checkoutSettingsGroup as CheckoutSettingsGroup)
      ),
      priceSettingsGroup: this.portalFeatureService.getPortalFeatureGroup('priceSettingsGroup').pipe(
        first(),
        map(priceSettingsGroup => priceSettingsGroup as PriceSettingsGroup)
      ),
      cust: this.selectCustomerService.getSelectedCustomer().pipe(first())
    })
      .pipe(
        takeUntil(this.unsubscribeTotals),
        switchMap(({ isCustomerService, checkoutSettingsGroup, priceSettingsGroup, cust }) => {
          this.openCustomerTextPopup(cust, isCustomerService);
          return combineLatest({
            checkoutSettingsGroup: of(checkoutSettingsGroup).pipe(first()),
            priceSettingsGroup: of(priceSettingsGroup).pipe(first()),
            additionalCostTerms: checkoutSettingsGroup.useTradePolicy
              ? this.hlDisplayAdditionalCostsService.getAdditionalCostTerms(cust).pipe(first())
              : of(null),
            customFreight: this.hlDisplayFreightService.getCustomFreight(cust).pipe(first()),
            totals: this.cartTotalsService.getTotals().pipe(takeUntil(this.unsubscribeTotals))
          });
        })
      )
      .subscribe(({ checkoutSettingsGroup, priceSettingsGroup, additionalCostTerms, customFreight, totals }) => {
        this.minOrderValue = checkoutSettingsGroup.minimumOrderValue;

        if (totals.totalCurrency !== '') {
          this.currencyCode = totals.totalCurrency;
        } else {
          this.currencyCode = priceSettingsGroup.defaultCurrencyCode;
        }

        const currencySymbol = this.hlDisplayAdditionalCostsService.getCurrencySymbol(this.currencyCode);

        this.hlDisplayCheckoutUtilService.emitSkipAdminFeeEvent(!checkoutSettingsGroup.applyAdminCharge);

        if (currencySymbol) {
          this.currencySymbol = currencySymbol;
        }

        this.orderTotalValue = totals.totalValue;

        if (additionalCostTerms != null) {
          const freightCostItem = this.hlDisplayAdditionalCostsService.getFreightCharge(
            this.orderTotalValue,
            additionalCostTerms
          );

          if (freightCostItem) {
            this.freightCost = freightCostItem.value;
            this.freightCostMin = freightCostItem.freightMin;
          }

          const adminFeeItem = this.hlDisplayAdditionalCostsService.getAdminFee(
            this.orderTotalValue,
            additionalCostTerms
          );

          // Exclude admin charge if it is not enabled in the checkout settings
          if (adminFeeItem && checkoutSettingsGroup.applyAdminCharge) {
            this.adminCharge = adminFeeItem.value;
            this.adminChargeMin = adminFeeItem.adminChargeMin;
          }

          const minOrderItem = this.hlDisplayAdditionalCostsService.getMinOrder(additionalCostTerms);

          if (minOrderItem) {
            this.minOrderValue = minOrderItem.value;
          }
        } else if (!!customFreight && customFreight.freightLevels.length > 0) {
          this.customFreight = customFreight;
        }
      });
    this.portalFeatureService.getPortalFeatureGroup('orderCheckoutSettingsGroup').subscribe(settings => { });

    this.creditBlockedService
      .checkCreditBlockedSelectedCustomer()
      .pipe(first())
      .subscribe(result => {
        this.customer = result.customer;

        if (result.status === 'CREDIT_HARD_BLOCK') {
          this.isCreditHardBlock = true;
          this.isCreditSoftBlock = false;
        }

        if (result.status === 'CREDIT_SOFT_BLOCK' && result.credit) {
          this.creditLeft = Number(result.credit?.creditLeft) || 0;

          this.cartTotalsService
            .getTotals()
            .pipe(takeUntil(this.unsubscribeTotals))
            .subscribe(totalsObject => {
              this.totals = totalsObject;

              if (this.creditLeft < this.totals.totalValue && !this.modalOpen) {
                // Open Modal
                this.modalOpen = true;
                const options: NgbModalOptions = {
                  backdrop: 'static',
                  size: 'sm',
                  keyboard: true
                };

                const translation = this.creditBlockedService.getPortalTranslationCart(
                  result.customer,
                  this.creditLeft,
                  this.totals.totalValue
                );

                this.gungModalService.openBasicModal(translation.title, translation.body, options).then(
                  result => {
                    this.isCreditSoftBlock = true;
                  },
                  reason => {
                    this.isCreditSoftBlock = true;
                  }
                );
              }

              if (this.creditLeft >= this.totals.totalValue) {
                this.modalOpen = false;
                this.isCreditSoftBlock = false;
              }
            });
        }
      });
  }

  protected renderDynamicComponents(): void {
    super.renderDynamicComponents();
    let hlButtons = this.renderedButtons as HlCheckoutButtonsPortalComponent;
    hlButtons.additionalCosts = this.currentCheckout?.extra?.additionalCosts;
    hlButtons.totalDiscount = this.currentCheckout?.extra?.oh?.kundrabatt;
    this.changeDetectorRef.detectChanges();
    hlButtons.totalDiscountOutput.pipe(takeUntil(this.unsubscribeTotals)).subscribe(totalDiscount => {
      this.currentCheckout.extra.oh.kundrabatt = totalDiscount;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribeTotals.next();
    this.unsubscribeTotals.complete();
  }

  // Apply discount that has been set through the checkout buttons
  protected applyChangesFromDynamicComponents(
    oldCheckout: CheckoutObject,
    newCheckout: CheckoutObject
  ): CheckoutObject {
    if (oldCheckout === undefined) {
      return newCheckout;
    }

    const result = { ...newCheckout };

    if (oldCheckout.extra?.oh?.kundrabatt !== undefined || result.extra.oh !== undefined) {
      result.extra.oh.kundrabatt = oldCheckout.extra?.oh?.kundrabatt;
    }

    return result;
  }

  protected openCustomerTextPopup(customer: Customer, isCustomerService: boolean): void {
    if (isCustomerService && !!customer?.extra?.kus?.edit) {
      const html: string = `<pre style="font-size: inherit;font-weight: bold;font-family: inherit;">${customer.extra.kus.edit.replace(/\n{2,}/g, '\n')}</pre>`
      this.gungModalService.openBasicModal(this.translateService.instant('INTERNAL_TEXT'), html, {}, false, 'OK').then(() => { }, () => { });
    }
  }
}
